import { IHistoryProvider, Answers, HistoryProviderOptions, HistoryMetaData } from "./IProvider";
import { deleteChatHistoryApi, getChatHistoryApi, getChatHistoryListApi, postChatHistoryApi } from "../../api";

export class BlobStorageProvider implements IHistoryProvider {
    getProviderName = () => HistoryProviderOptions.BlobStorage;

    private continuationToken: string | undefined;
    private isItemEnd: boolean = false;

    resetContinuationToken() {
        this.continuationToken = undefined;
        this.isItemEnd = false;
    }

    async getNextItems(count: number, idToken?: string): Promise<HistoryMetaData[]> {
        if (this.isItemEnd) {
            return [];
        }

        try {
            const response = await getChatHistoryListApi(count, this.continuationToken, idToken || "");
            this.continuationToken = response.continuation_token;
            if (!this.continuationToken) {
                this.isItemEnd = true;
            }
            return response.sessions.map(session => {
                let ts = session.timestamp;
                // Ensure timestamp is a valid number in milliseconds
                if (typeof ts === "string") {
                    ts = Number(ts);
                }
                if (typeof ts !== "number" || isNaN(ts)) {
                    ts = Date.now(); // fallback to now if missing
                }
                // If timestamp is in seconds, convert to ms (assume anything before year 2001 is seconds)
                if (ts < 100000000000) {
                    ts = ts * 1000;
                }
                return {
                    id: session.id,
                    title: session.title || "(No Subject)",
                    timestamp: ts
                };
            });
        } catch (e) {
            console.error(e);
            return [];
        }
    }

    async addItem(id: string, answers: Answers, idToken?: string): Promise<void> {
        // Transform answers (array of [user, response]) into messages array for backend
        const messages = answers
            .map(([user, response]) => {
                // Parse serialized user message back to {subject, body} object
                // so the backend title generator receives a proper dict, not a raw JSON string
                let userContent: any = user;
                if (typeof user === "string") {
                    try {
                        const parsed = JSON.parse(user);
                        if (parsed && typeof parsed === "object" && ("subject" in parsed || "body" in parsed)) {
                            userContent = parsed;
                        }
                    } catch {
                        // Keep as string if not valid JSON
                    }
                }
                return [
                    { role: "user", content: userContent },
                    { role: "assistant", content: response }
                ];
            })
            .flat();
        // Use session_id instead of id to match backend expectation
        await postChatHistoryApi({ session_id: id, messages }, idToken || "");
        return;
    }

    async getItem(id: string, idToken?: string): Promise<Answers | null> {
        try {
            const response = await getChatHistoryApi(id, idToken || "");

            // If the response already has answers (browser format), return it
            if (response.answers) {
                return response.answers;
            }

            // If the response has messages (blob format), transform it
            if (response.messages && Array.isArray(response.messages)) {
                const answers: Answers = [];

                // Process messages in pairs (user message followed by assistant message)
                for (let i = 0; i < response.messages.length; i += 2) {
                    const userMessage = response.messages[i];
                    const assistantMessage = response.messages[i + 1];

                    if (userMessage && assistantMessage && userMessage.role === "user" && assistantMessage.role === "assistant") {
                        // Extract user content (could be string or JSON)
                        let userContent = userMessage.content || "";
                        if (typeof userContent === "object") {
                            userContent = JSON.stringify(userContent);
                        }

                        // Extract assistant response - handle the nested structure from your data
                        let assistantResponse: any;

                        if (typeof assistantMessage.content === "object" && assistantMessage.content !== null) {
                            // This handles the complex structure you showed
                            const content = assistantMessage.content as any;
                            assistantResponse = {
                                message: {
                                    content: content.message?.content || content.message || String(assistantMessage.content),
                                    role: "assistant"
                                },
                                delta: {
                                    content: "",
                                    role: "assistant"
                                },
                                context: content.context || {
                                    data_points: [],
                                    followup_questions: [],
                                    thoughts: []
                                },
                                session_state: content.session_state || null
                            };
                        } else {
                            // Simple string content fallback
                            assistantResponse = {
                                message: {
                                    content: String(assistantMessage.content || ""),
                                    role: "assistant"
                                },
                                delta: {
                                    content: "",
                                    role: "assistant"
                                },
                                context: {
                                    data_points: [],
                                    followup_questions: [],
                                    thoughts: []
                                },
                                session_state: null
                            };
                        }

                        answers.push([userContent, assistantResponse]);
                    }
                }

                return answers;
            }

            return null;
        } catch (error) {
            console.error("Error retrieving chat history:", error);
            return null;
        }
    }

    async deleteItem(id: string, idToken?: string): Promise<void> {
        await deleteChatHistoryApi(id, idToken || "");
        return;
    }
}
